import {
  AUTH_MANAGER_LEGACY_CHANGE_EVENT,
  LEGACY_PROFILE_STORAGE_KEY,
} from "./config";

import type { LegacyUserProfile } from "../types";

import { LegacyProfileFetchError } from "./errors/legacy-profile-fetch";

const LEGACY_PROFILE_ENDPOINT = "/api/profile/me";

/**
 * **V1-only.** Gerencia o profile enriquecido (role, equipe, is_aprovador,
 * etc.) consumido por componentes legados. Isolada do `AuthManager` —
 * recebe o access token como parâmetro em `load(accessToken)`.
 *
 * Quando a v1 for descomissionada, esta classe (e a rota `/api/profile/me`)
 * podem ser deletadas sem afetar a v2.
 */
export class AuthManagerLegacy {
  private static instance: AuthManagerLegacy | null = null;
  private profile: LegacyUserProfile | null = null;
  private readonly storageKey: string;

  private constructor() {
    this.storageKey = LEGACY_PROFILE_STORAGE_KEY;
    this.loadFromStorage();
  }

  static getInstance(): AuthManagerLegacy {
    if (!AuthManagerLegacy.instance) {
      AuthManagerLegacy.instance = new AuthManagerLegacy();
    }
    return AuthManagerLegacy.instance;
  }

  // ─── Leitura ───────────────────────────────────────────────────────────

  getProfile(): LegacyUserProfile | null {
    return this.profile;
  }

  // ─── Mutação ───────────────────────────────────────────────────────────

  /**
   * Busca o profile em `/api/profile/me` e persiste. O caller é quem fornece
   * o access token — esta classe não conhece o `AuthManager` de propósito.
   *
   * Falha (rede, HTTP non-2xx, JSON inválido) lança `LegacyProfileFetchError`.
   * Tratamento de falha (logout/retry/etc.) é responsabilidade do caller.
   */
  async load(accessToken: string): Promise<LegacyUserProfile> {
    if (!accessToken) {
      throw new LegacyProfileFetchError(
        "Access token vazio ao buscar legacy profile",
      );
    }

    let response: Response;
    try {
      response = await fetch(LEGACY_PROFILE_ENDPOINT, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    } catch (error) {
      throw new LegacyProfileFetchError(
        `Erro de rede ao buscar legacy profile: ${(error as Error).message}`,
      );
    }

    if (!response.ok) {
      throw new LegacyProfileFetchError(
        `Legacy profile retornou HTTP ${response.status}`,
      );
    }

    let profile: LegacyUserProfile;
    try {
      profile = (await response.json()) as LegacyUserProfile;
    } catch {
      throw new LegacyProfileFetchError(
        "Legacy profile retornou JSON inválido",
      );
    }

    this.profile = profile;
    this.persist(profile);
    this.dispatchChange();
    return profile;
  }

  clear(): void {
    this.profile = null;
    this.persist(null);
    this.dispatchChange();
  }

  // ─── Privados ──────────────────────────────────────────────────────────

  private loadFromStorage(): void {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(this.storageKey);
      this.profile = raw ? (JSON.parse(raw) as LegacyUserProfile) : null;
    } catch {
      this.profile = null;
    }
  }

  private persist(profile: LegacyUserProfile | null): void {
    if (typeof window === "undefined") return;
    if (profile) {
      window.localStorage.setItem(this.storageKey, JSON.stringify(profile));
    } else {
      window.localStorage.removeItem(this.storageKey);
    }
  }

  private dispatchChange(): void {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(AUTH_MANAGER_LEGACY_CHANGE_EVENT));
  }
}
